import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { listUsers } from "@/lib/api/users";
import { cn } from "@/lib/utils";
import type { User } from "@/types";

// L'API plafonne une page à 100 comptes : on les charge toutes.
const PAGE_SIZE = 100;

async function listAllUsers(): Promise<User[]> {
  const first = await listUsers({ page: 1, pageSize: PAGE_SIZE });
  const rest = await Promise.all(
    Array.from({ length: Math.max(first.total_pages - 1, 0) }, (_, i) =>
      listUsers({ page: i + 2, pageSize: PAGE_SIZE })
    )
  );
  return [first, ...rest].flatMap((p) => p.items);
}

function displayName(user: User): string {
  return user.full_name?.trim() || user.email;
}

function normalize(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

// Chaque mot tapé doit figurer dans le nom ou l'adresse, sans casse ni accents :
// la recherche approchée par défaut de cmdk remonte des comptes sans rapport.
function matchUser(value: string, search: string, keywords?: string[]): number {
  const haystack = normalize([value, ...(keywords ?? [])].join(" "));
  return normalize(search)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word))
    ? 1
    : 0;
}

interface UserFilterProps {
  /** « all » ou l'identifiant de l'utilisateur retenu */
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

/**
 * Filtre « Utilisateur » des listes : on tape un nom, un prénom ou une adresse
 * pour retrouver le compte, accents facultatifs.
 */
export function UserFilter({ value, onChange, className }: UserFilterProps) {
  const [open, setOpen] = useState(false);

  const { data: users = [] } = useQuery({
    queryKey: ["users-filter-all"],
    queryFn: listAllUsers,
  });

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) =>
        displayName(a).localeCompare(displayName(b), "fr", {
          sensitivity: "base",
        })
      ),
    [users]
  );

  const selected =
    value === "all" ? undefined : users.find((u) => String(u.id) === value);

  const choose = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-[200px] justify-between font-normal", className)}
        >
          <span className="truncate">
            {selected ? displayName(selected) : "Tous les utilisateurs"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command filter={matchUser}>
          <CommandInput placeholder="Nom, prénom ou e-mail..." />
          <CommandList>
            <CommandEmpty>Aucun utilisateur trouvé.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="Tous les utilisateurs"
                onSelect={() => choose("all")}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === "all" ? "opacity-100" : "opacity-0"
                  )}
                />
                Tous les utilisateurs
              </CommandItem>
              {sortedUsers.map((user) => {
                const name = user.full_name?.trim() ?? "";
                return (
                  <CommandItem
                    key={user.id}
                    value={`${displayName(user)} ${user.email}`}
                    keywords={user.pseudo ? [user.pseudo] : undefined}
                    onSelect={() => choose(String(user.id))}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 shrink-0",
                        value === String(user.id) ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate">{displayName(user)}</span>
                      {name && (
                        <span className="truncate text-xs opacity-70">
                          {user.email}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
